import {
  Alert,
  Flex,
  ProviderConsumer,
  Text,
  TextArea
} from '@fluentui/react-northstar';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TicketSVG from '../../../../../svg/ticket.svg';
import { appState } from '../../../../AppState';
import { threedigitized } from '../../../../tikit/ticketHelper';
import { ThemeColorScheme } from '../../../common/TeamsTheme';
import { LICENSE_FEATURE } from '../../../utils/constants';
import { CheckFeature } from '../../CheckFeature';
import { TypeaheadPicker } from '../../NestedDropdown/TypeaheadPicker';
import { TypeaheadItemPicker } from '../../NestedDropdown/TypeaheadItemPicker';
import { MultiTypeaheadField } from '../../table/TypeaheadField';
import { FieldErrorText } from '../../FieldErrorText';
import { LabelElement } from '../../TicketForm';
import { formatAssigneeItems, formatSupportGroupItems, formatTeamItems } from '../TaskCommon/TaskCommon';
import './TicketTasksDialog.css';

interface Props {
  statuses: TaskStatus[];
  analysts: PlatformUser[];
  ticketId: number;
  ticketTitle: string;
  setFormObj: any;
  formObj: any;
  alert: any;
  isCurrentPhase: boolean;
}

export const TicketTasksForm = ({
  statuses,
  analysts,
  ticketId,
  ticketTitle,
  setFormObj,
  formObj,
  alert,
  isCurrentPhase
}: Props) => {
  
  const { t } = useTranslation();
  const currentState = appState();

  const [analystsItems, setAnalystsItems] = useState([]);
  const [supportGroupDropDownItems, setSupportGroupDropDownItems] = useState([]);
  const [teamDropDownItems, setTeamDropDownItems] = useState([]);
  /**
   * Required fields the agent has emptied: their message shows under the field, as on the ticket forms - only once
   * something that was filled in is cleared, not on a dialog that opens blank.
   */
  const [emptied, setEmptied] = useState({ title: false, status: false });
  const markEmptied = (field: keyof typeof emptied, wasEmpty: boolean, isEmpty: boolean) => {
    if (!wasEmpty && isEmpty) setEmptied(prev => (prev[field] ? prev : { ...prev, [field]: true }));
  };
  const requiredMessage = (field: keyof typeof emptied, isEmpty: boolean) => emptied[field] && isEmpty
    ? <FieldErrorText>{t('ticket-details.edit-ticket-view.required-field-must-be-filled-out')}</FieldErrorText>
    : null;

  // A new task has none yet: the default status is then reported as the choice, as before. Once the agent clears it,
  // it is given as empty - left undefined, the picker put the default straight back.
  const statusDataItem = () => {
    if (formObj?.Status) return { ...formObj.Status, id: formObj.Status.Id, value: formObj.Status.Value };
    if (emptied.status) return { id: null, parentId: null, position: 0, value: '' };
    return undefined;
  };

  const findDefaultValue = (itemsArray: any[]) => {
    return itemsArray.find(item => item.IsDefault);
  };

  const populateTeamsDropDown = (teams: any[]) => {
    const items = teams.map((team: any) => {
      if (team.accessibilityitemprops.id === formObj?.TeamId) {
        team.selected = true;
      } else {
        team.selected = false;
      }
      return team;
    });
    setTeamDropDownItems([...items]);
  }

  const populateSupportGroupDropDown = (groups: any[]) => {
    const groupItems = groups.map((group: any) => {
      if (group.accessibilityitemprops.id === formObj?.SupportGroupId) {
        group.selected = true;
      } else {
        group.selected = false;
      }
      return group;
    });
    setSupportGroupDropDownItems([...groupItems]);
  }

  const populateAssigneeDropDown = ()=>{
    const supportGroup = currentState.supportgroups.filter(x=>x.Id == formObj?.SupportGroupId)[0];
    const users = supportGroup ? supportGroup.Members : analysts;
    const items = users.map((data: any) => {
      if (data.Id === formObj?.Assignee?.Id) {
        data.selected = true;
      } else {
        data.selected = false;
      }
      data.header = data.FullName;
      // The picker knows an option by this, as the formatted team and group items carry it.
      data.accessibilityitemprops = data.accessibilityitemprops ?? { id: data.Id };
      return data;
    });
    setAnalystsItems([...items]);
  }

  const onTeamChange = (selectedTeam: any) => {
    const updatedTeamItems = teamDropDownItems.map((item: any) => {
      if (item.Id == selectedTeam?.Id) {
        item.selected = true;
      } else {
        item.selected = false;
      }
      return item;
    });
    setTeamDropDownItems(updatedTeamItems);

    const updatedSupportGroupItems = selectedTeam ? currentState.supportgroups.filter(t => t.TeamsAadObjectId == selectedTeam?.TeamsAadObjectId) : currentState.supportgroups;
    setSupportGroupDropDownItems(formatSupportGroupItems(updatedSupportGroupItems));

    const updatedAssigneeItems = (!selectedTeam) ? analysts : currentState.teams.find(x => x.Id == selectedTeam?.Id)?.Users || [];
    setAnalystsItems(formatAssigneeItems(updatedAssigneeItems));

    setFormObj({
      ...formObj,
      TeamId: selectedTeam?.Id,
      Team: selectedTeam,
      SupportGroupId: null,
      SupportGroup: null,
      AssigneeId: null,
      Assignee: null
    });
  }

  const onGroupChange = (selectedGroup: any) => {
    const updatedGroupItems = supportGroupDropDownItems.map((item: any) => {
      if (item.Id == selectedGroup?.Id) {
        item.selected = true;
      } else {
        item.selected = false;
      }
      return item;
    });
    setSupportGroupDropDownItems(updatedGroupItems);

    const updatedAssigneeItems = (selectedGroup) ? selectedGroup?.Members : analysts;
    setAnalystsItems(formatAssigneeItems(updatedAssigneeItems));

    setFormObj({
      ...formObj,
      SupportGroupId: selectedGroup?.Id,
      SupportGroup: selectedGroup,
      AssigneeId: null,
      Assignee: null
    });
  }

  const onAssigneeChange = (selectedAssignee: any) => {
    const updatedAssigneeItems = analystsItems.map((item: any) => {
      if (item.Id == selectedAssignee?.Id) {
        item.selected = true;
      } else {
        item.selected = false;
      }
      return item;
    });
    setAnalystsItems(updatedAssigneeItems);
    setFormObj({
      ...formObj,
      AssigneeId: selectedAssignee?.Id,
      Assignee: selectedAssignee
    });
  }

  useEffect(()=>{
    populateTeamsDropDown(formatTeamItems(currentState.teams));
  },[formObj?.TeamId]);

  useEffect(()=>{
    let  filteredGroups = currentState.supportgroups;
    if(formObj?.Team && formObj?.Team!== null){
      filteredGroups=currentState.supportgroups.filter(t => t.TeamsAadObjectId == formObj?.Team?.TeamsAadObjectId) ;
    }
    populateSupportGroupDropDown(formatSupportGroupItems(filteredGroups));
  },[formObj?.SupportGroupId]);

  useEffect(()=>{
    populateAssigneeDropDown();
  },[formObj?.AssigneeId]);

  // Everyone who can be a collaborator but the assignee - as the old list offered.
  const collaboratorOptions = useMemo(() => (currentState.usersInRole ?? [])
    .filter(user => user.Id !== formObj?.AssigneeId)
    .map(user => ({ id: user.Id, name: user.FullName })), [currentState.usersInRole, formObj?.AssigneeId]);

  const onChangeCollaboratorIds = (ids: number[]) =>
    setFormObj({ ...formObj, TaskCollaborators: ids.map(id => ({ PlatformUserId: id })) });

  return (
    <ProviderConsumer
      render={globalTheme => (
        <>
          <div className="dialog-header font-bold" style={{ marginTop: '-30px' }}>
            <Flex vAlign="center">
              <TicketSVG width={24} className={`mr-1`} />
              <Text style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }}>
                #{threedigitized(ticketId)}
              </Text>
            </Flex>
            <div className={`py-2 ticketTitle`} style={{ fontSize: '12px', ...ThemeColorScheme(globalTheme.siteVariables) }}>
              {ticketTitle}
            </div>
          </div>
          <div>
            <div className={`my-2 font-bold`}>{`${(formObj.Id > 0) ? t('ticket-details.edit-ticket-form.task.dialog.update-task') : t('ticket-details.edit-ticket-form.task.dialog.add-task')}`}</div>
            <div className={`my-2`}>
              {alert.show && (
                <Alert
                  fitted
                  {...alert.attributes}
                  content={alert.title}
                  style={{ fontSize: 12, width: '100%' }}
                />
              )}
            </div>
            <div>
              <div className={`mt-2`} style={{ marginBottom: '-0.25rem' }}>
                <LabelElement label={t('ticket-details.edit-ticket-form.task.fields.title')} required={true} themeAsterisk>
                  <TextArea
                    fluid
                    maxLength={500}
                    required
                    style={{ height: 60 }}
                    onChange={(_e, data) => {
                      markEmptied('title', !formObj.Title?.trim(), !data.value?.trim());
                      setFormObj({ ...formObj, Title: data.value });
                    }}
                    value={formObj.Title}
                  />
                  {requiredMessage('title', !formObj.Title?.trim())}
                </LabelElement>
              </div>

              {(isCurrentPhase) && (
                <div className={`mt-2`}>
                  <LabelElement label={t('ticket-details.edit-ticket-form.task.fields.status')} required themeAsterisk>
                    <TypeaheadPicker
                      ariaLabel={t('ticket-details.edit-ticket-form.task.fields.status')}
                      items={statuses.map((item: TaskStatus) => ({
                        id: item.Id,
                        parentId: item.ParentId,
                        position: item.Position,
                        value: item.Value,
                        ...item
                      }))}
                      dataItem={statusDataItem()}
                      defaultValue={findDefaultValue(statuses)?.Value}
                      showClearBtn
                      onChange={(value: any) => {
                        markEmptied('status', !formObj.StatusId, !value);
                        setFormObj({
                          ...formObj,
                          Status: value ?? null,
                          StatusId: value?.Id ?? null
                        });
                      }}
                    />
                    {requiredMessage('status', !formObj.StatusId)}
                  </LabelElement>
                </div>
              )}

              <CheckFeature featureName={LICENSE_FEATURE.MultiDepartment}>
                  <div className={`mt-2`}>
                    <LabelElement label={t('ticket.add-ticket.team')} >
                    <TypeaheadItemPicker
                      label={t('ticket.add-ticket.team')}
                      items={teamDropDownItems}
                      valueId={formObj?.TeamId}
                      valueName={formObj?.Team?.Name}
                      clearable
                      onChange={item => onTeamChange(item)}
                    />
                    </LabelElement>
                  </div>
              </CheckFeature>

              <div className={`mt-2`}>
                  <Text content={t('ticket.add-ticket.group')} />
                  <TypeaheadItemPicker
                    label={t('ticket.add-ticket.group')}
                    items={supportGroupDropDownItems}
                    valueId={formObj?.SupportGroupId}
                    valueName={formObj?.SupportGroup?.Name}
                    clearable
                    onChange={item => onGroupChange(item)}
                  />
               </div>

              <div className={`mt-2`}>
                <LabelElement label={t('ticket-details.edit-ticket-form.task.fields.assignee')}>
                  <TypeaheadItemPicker
                    label={t('ticket-details.edit-ticket-form.task.fields.assignee')}
                    items={analystsItems}
                    valueId={formObj?.AssigneeId}
                    valueName={formObj?.Assignee?.FullName}
                    clearable
                    onChange={item => onAssigneeChange(item)}
                  />
                </LabelElement>
              </div>

              <CheckFeature featureName={LICENSE_FEATURE.Collaborators}>
                <div className={`mt-2`}>
                  <Text content={t('ticket.ticket-list.collaborators')} />
                  <MultiTypeaheadField
                    label={t('ticket.ticket-list.collaborators')}
                    hideLabel
                    listFitsBox
                    placeholder={t('ticket.add-ticket.collaborators')}
                    options={collaboratorOptions}
                    // Only people the box can name - an empty chip helps no one.
                    valueIds={(formObj?.TaskCollaborators ?? []).map((c: any) => c.PlatformUserId)
                      .filter((id: number) => collaboratorOptions.some(option => option.id === id))}
                    onChange={onChangeCollaboratorIds}
                  />
                </div>
                </CheckFeature >
            </div>
          </div>
        </>
      )}
    />
  );
};
